import { useState, useEffect, useCallback } from "react";
import { Newspaper, Plus, Edit, Trash2, Eye, EyeOff, Save, RefreshCw, Clock, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterManager = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cron, setCron] = useState({ enabled: false, interval_hours: 6, last_run: "" });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter-archive`);
      setNewsletters(res.data || []);
    } catch (e) { console.error("Newsletter load error:", e); }
  };

  const loadCron = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletters/cron`);
      setCron(res.data);
    } catch (e) { console.error("Cron load error:", e); }
  }, []);

  useEffect(() => { load(); loadCron(); }, [loadCron]);

  const toggleCron = async () => {
    try {
      await axios.post(`${API}/admin/newsletters/cron`, { enabled: !cron.enabled, interval_hours: cron.interval_hours });
      setCron(prev => ({ ...prev, enabled: !prev.enabled }));
      toast.success(!cron.enabled ? "Cronjob aktiviert" : "Cronjob deaktiviert");
    } catch (e) { console.error(e); toast.error("Fehler"); }
  };

  const [pulling, setPulling] = useState(false);

  const pullNow = async () => {
    setPulling(true);
    try {
      const res = await axios.post(`${API}/admin/newsletters/pull-now`);
      if (res.data.success) {
        toast.success(`${res.data.count} Newsletter von Atlas abgerufen`);
        load();
        loadCron();
      } else {
        toast.error(res.data.error || "Pull fehlgeschlagen");
      }
    } catch (e) { console.error(e); toast.error("Fehler beim Abrufen"); }
    setPulling(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/newsletter-archive/${editing.id}`, editing);
      } else {
        await axios.post(`${API}/admin/newsletter-archive`, editing);
      }
      toast.success("Newsletter gespeichert!");
      setEditing(null);
      load();
    } catch (e) { console.error(e); toast.error("Fehler beim Speichern"); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Newsletter löschen?")) return;
    try {
      await axios.delete(`${API}/admin/newsletter-archive/${id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) { console.error(e); toast.error("Fehler"); }
  };

  if (editing) {
    return (
      <div className="space-y-4" data-testid="newsletter-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-playfair text-lg font-semibold text-lab-primary">
            {editing.id ? "Newsletter bearbeiten" : "Neuer Newsletter"}
          </h3>
          <button onClick={() => setEditing(null)} className="text-sm text-lab-text-muted hover:text-lab-primary">
            Abbrechen
          </button>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-lab-primary">Titel</label>
            <Input value={editing.title || ""} onChange={(e) => setEditing({...editing, title: e.target.value})} className="rounded-none border-lab-border" data-testid="newsletter-title-input" placeholder="z.B. KI-Trends Q1 2026" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-lab-primary">Kategorie</label>
              <select value={editing.category || "Allgemein"} onChange={(e) => setEditing({...editing, category: e.target.value})} className="w-full h-10 px-3 border border-lab-border bg-white text-sm" data-testid="newsletter-category-select">
                <option>Allgemein</option>
                <option>KI & Automatisierung</option>
                <option>Digitalisierung</option>
                <option>Prozessoptimierung</option>
                <option>Branchentrends</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-lab-primary">Status</label>
              <select value={editing.status || "draft"} onChange={(e) => setEditing({...editing, status: e.target.value})} className="w-full h-10 px-3 border border-lab-border bg-white text-sm" data-testid="newsletter-status-select">
                <option value="draft">Entwurf</option>
                <option value="published">Veröffentlicht</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-lab-primary">Zusammenfassung (für Vorschau)</label>
            <textarea value={editing.summary || ""} onChange={(e) => setEditing({...editing, summary: e.target.value})} className="w-full px-3 py-2 border border-lab-border text-sm min-h-[60px] resize-y" data-testid="newsletter-summary-input" placeholder="Kurze Zusammenfassung für die Archiv-Übersicht..." />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-lab-primary">Inhalt (Markdown)</label>
            <textarea value={editing.content || ""} onChange={(e) => setEditing({...editing, content: e.target.value})} className="w-full px-3 py-2 border border-lab-border text-sm min-h-[300px] resize-y font-mono" data-testid="newsletter-content-input" placeholder="Newsletter-Inhalt in Markdown..." />
          </div>
        </div>

        <button onClick={save} disabled={saving || !editing.title} className="btn-primary rounded-none flex items-center gap-2 px-6 disabled:opacity-50" data-testid="newsletter-save-btn">
          <Save className="w-4 h-4" />
          {saving ? "Speichern..." : "Speichern"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="newsletter-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-lab-accent" />
          <h3 className="font-semibold text-lab-primary">Newsletter-Archiv</h3>
          <span className="text-xs bg-lab-bg-light text-lab-text-muted px-2 py-0.5 border border-lab-border">{newsletters.length}</span>
        </div>
        <button onClick={() => setEditing({ title: "", summary: "", content: "", category: "Allgemein", status: "draft" })} className="btn-primary rounded-none text-sm flex items-center gap-1.5 px-4" data-testid="newsletter-create-btn">
          <Plus className="w-4 h-4" /> Neuer Newsletter
        </button>
      </div>

      <p className="text-sm text-lab-text-muted">Veröffentlichte Newsletter erscheinen auf der Website unter /newsletter und auf der Startseite.</p>

      {/* Cron-Einstellungen */}
      <div className="p-4 border border-lab-border bg-lab-bg-light" data-testid="newsletter-cron-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RefreshCw className={`w-4 h-4 ${cron.enabled ? "text-green-600 animate-spin" : "text-lab-text-muted"}`} style={cron.enabled ? { animationDuration: "3s" } : {}} />
            <div>
              <span className="text-sm font-semibold text-lab-primary">Atlas OS Auto-Pull</span>
              <p className="text-xs text-lab-text-muted">
                {cron.enabled ? `Aktiv – alle ${cron.interval_hours}h` : "Deaktiviert"}
                {cron.last_run && <span className="ml-2 inline-flex items-center gap-1"><Clock className="w-3 h-3" />Letzter Lauf: {new Date(cron.last_run).toLocaleString("de-CH")}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={pullNow}
              disabled={pulling}
              data-testid="newsletter-pull-now"
              className="px-4 py-1.5 text-xs font-semibold border border-lab-border text-lab-primary hover:bg-white transition-colors disabled:opacity-50 flex items-center gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${pulling ? "animate-spin" : ""}`} />
              {pulling ? "Abrufen..." : "Jetzt abrufen"}
            </button>
            <button
              onClick={toggleCron}
              data-testid="newsletter-cron-toggle"
              className={`px-4 py-1.5 text-xs font-semibold border transition-colors ${cron.enabled ? "border-red-300 text-red-600 hover:bg-red-50" : "border-green-300 text-green-700 hover:bg-green-50"}`}
            >
              {cron.enabled ? "Deaktivieren" : "Aktivieren"}
            </button>
          </div>
        </div>
      </div>

      {newsletters.length === 0 ? (
        <p className="text-lab-text-muted text-sm py-8 text-center">Noch keine Newsletter erstellt.</p>
      ) : (
        <div className="space-y-2">
          {newsletters.map((nl) => (
            <div key={nl.id} className="flex items-center justify-between p-4 border border-lab-border bg-white" data-testid={`newsletter-item-${nl.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-lab-primary text-sm truncate">{nl.title}</h4>
                  {nl.status === "published" ? (
                    <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 border border-green-200 flex items-center gap-1"><Eye className="w-3 h-3" />Live</span>
                  ) : (
                    <span className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 border border-gray-200 flex items-center gap-1"><EyeOff className="w-3 h-3" />Entwurf</span>
                  )}
                  <span className="text-xs text-lab-text-muted">{nl.category}</span>
                  {nl.sent_to_subscribers && (
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 border border-blue-200 flex items-center gap-1"><Send className="w-3 h-3" />{nl.sent_count} versendet</span>
                  )}
                </div>
                {nl.summary && <p className="text-xs text-lab-text-muted mt-1 truncate">{nl.summary}</p>}
                <p className="text-xs text-lab-text-muted mt-1">
                  {nl.published_at ? `Veröffentlicht: ${new Date(nl.published_at).toLocaleDateString("de-CH")}` : `Erstellt: ${new Date(nl.created_at).toLocaleDateString("de-CH")}`}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button onClick={() => setEditing({...nl})} className="p-2 text-lab-text-muted hover:text-lab-accent" data-testid={`newsletter-edit-${nl.id}`}>
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => remove(nl.id)} className="p-2 text-lab-text-muted hover:text-red-500" data-testid={`newsletter-delete-${nl.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsletterManager;
